"use client";

import { Spinner } from "@/components/ui/atoms/Spinner";
import "@/components/ui/molecules/categories/feedback.css";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/shadcn/alert-dialog";

import { memo, type ReactNode } from "react";

interface ConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description: ReactNode;
    /** Optional content rendered between the description and the footer buttons. */
    children?: ReactNode;
    /** ReactNode, not string — a confirm label may carry a leading icon (the
     *  round-send confirm ships a `<Send />` beside its words). */
    confirmLabel?: ReactNode;
    cancelLabel?: string;
    variant?: "destructive" | "warning" | "default";
    /** RESOLVE to close the dialog; THROW (after surfacing your own error, e.g.
     *  a toast) to keep it open so the user's selection survives a retry. The
     *  thrown value is swallowed here — surface it in the handler, don't rely
     *  on it reaching a global error boundary. */
    onConfirm: () => void | Promise<void>;
    busy?: boolean;
    /** When true, the confirm button is disabled regardless of loading state. */
    confirmDisabled?: boolean;
    /** Class(es) for the portaled AlertDialogContent — the content renders at
     *  <body>, outside the caller's DOM scope, so surface-scoped token
     *  contexts (e.g. the portal's .dw-portal-scope) must ride the content
     *  element itself (divergence audit 2026-08-11). */
    contentClassName?: string;
}

/**
 * The one confirmation dialog. Every "are you sure" in the app renders this
 * tree; nothing else assembles AlertDialog Root → Content → Header → Title →
 * Description → Footer → Cancel + Action by hand.
 *
 * That tree used to be retyped at each callsite, and the copies drifted in the
 * places hand-typing always drifts: some wrapped the description so it could
 * hold markup and some left it a bare paragraph, some disabled the cancel
 * button while a request was in flight and some let the user dismiss a dialog
 * mid-write, and the confirm button's colour was whatever the button styling
 * happened to default to rather than a stated reading of the action. The last
 * hand-written copy was the automation-run cancel confirm in
 * AutomationRunsTable, folded in here 2026-09-20.
 *
 * This component decides the structure, the spinner and disabled-while-loading
 * behaviour, the description wrapper that lets `description` be any ReactNode,
 * and the close-on-resolve / stay-open-on-throw contract documented on
 * `onConfirm`. The caller still decides its own words (`title`,
 * `description`, `confirmLabel`, `cancelLabel`), how loud the confirm button
 * is (`variant` — note the default is "destructive", so a merely-consequential
 * action must say `variant="default"`), whether a busy state is worth showing
 * (`busy`), any extra body between description and footer (`children`),
 * and the token scope the portaled content lands in (`contentClassName`).
 */
export const ConfirmDialog = memo(function ConfirmDialog({ open, onOpenChange, title, description, children, confirmLabel = "Confirm", cancelLabel = "Cancel", variant = "destructive", onConfirm, busy = false, confirmDisabled = false, contentClassName }: ConfirmDialogProps) {
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange} data-component="ConfirmDialog" data-location="src/components/ui/molecules/ConfirmDialog.tsx">
            <AlertDialogContent className={contentClassName}>
                <AlertDialogHeader>
                    <AlertDialogTitle>{title}</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                        <div className="dw-confirm-dialog__description">{description}</div>
                    </AlertDialogDescription>
                </AlertDialogHeader>
                {children}
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={busy}>{cancelLabel}</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={async (e) => {
                            // Prevent AlertDialog from auto-closing so we can show loading state
                            e.preventDefault();
                            try {
                                await onConfirm();
                            } catch {
                                // The handler surfaced its own error — a FAILED
                                // confirm keeps the dialog open so the user's
                                // selections survive the retry (review 2026-08-11).
                                return;
                            }
                            onOpenChange(false);
                        }}
                        disabled={busy || confirmDisabled}
                        className="dw-confirm-dialog__action"
                        data-variant={variant}>
                        {busy && <Spinner size="sm" className="dw-confirm-dialog__spinner"/>}
                        {confirmLabel}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
});
